import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Header from '@/components-latest/layout/Header'
import Button from '@/components-latest/primitives/Button'
import Text from '@/components-latest/primitives/Text'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import {
  useCreateCartDiscountMutation,
  useEditCartDiscountMutation
} from '@/ducks/offers/api/offers.api'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import {
  useOfferFormNavigateBack,
  useOfferFormSuccessNavigate
} from '@/hooks/useOfferFormNavigateBack/useOfferFormNavigateBack'

import Styles from '../../OrdersPage/PaymentSummary/PaymentSummary.module.scss'
import ChooseCartDiscountDetails from './ChooseCartDiscountDetails/ChooseCartDiscountDetails'
type Props = {
  onSuccess?: () => void
  name?: string
}

type LocationState = {
  offerId?: string
  initialDiscount?: string
  initialExpiration?: string
  initialMinimumAmount?: string
  initialMaximumOff?: string
  initialDiscountType?: DiscountType
}

export enum DiscountType {
  Percentage = 'Percentage',
  FlatRate = 'Flat'
}

export enum Mode {
  Create = 'create',
  Edit = 'edit'
}

const DEFAULT_DISCOUNT_VALUE = '10'

const CreateCartDiscountForm = ({ onSuccess = () => {}, name }: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const { state } = location as { state?: LocationState }
  const {
    offerId,
    initialDiscount,
    initialExpiration,
    initialMinimumAmount,
    initialMaximumOff,
    initialDiscountType
  } = state || {}

  const [createCartDiscount, { isLoading }] = useCreateCartDiscountMutation()
  const [editCartDiscount, { isLoading: isEditCartLoading }] =
    useEditCartDiscountMutation()

  const mode = offerId ? Mode.Edit : Mode.Create
  const [discountType, setDiscountType] = useState<DiscountType>(
    initialDiscountType ?? DiscountType.Percentage
  )
  const [discountValue, setDiscountValue] = useState(
    initialDiscount ?? DEFAULT_DISCOUNT_VALUE
  )
  const [minimumAmount, setMinimumAmount] = useState(initialMinimumAmount ?? '')
  const [maximumOff, setMaximumOff] = useState(initialMaximumOff ?? '')
  const [expirationValue, setExpirationValue] = useState(
    initialExpiration ?? ''
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const currency = useSelector(selectCurrencyCode) || ''

  const handleGoBack = useOfferFormNavigateBack('cart_discount')
  const navigateOnSuccess = useOfferFormSuccessNavigate('cart_discount')

  const isFormValid = () => {
    if (discountType === DiscountType.Percentage) {
      return discountValue && minimumAmount && maximumOff && expirationValue
    } else if (discountType === DiscountType.FlatRate) {
      return (
        discountValue &&
        minimumAmount &&
        expirationValue &&
        parseFloat(minimumAmount) >= parseFloat(discountValue)
      )
    }
    return false
  }

  const isSubmitButtonDisabled = !isFormValid()

  const onOfferSubmit = () => {
    if (isSubmitButtonDisabled) return
    setIsModalOpen(true)
  }

  const submitEditDiscount = () => {
    const discountData = {
      discountId: offerId || '',
      discount: {
        discountType,
        amountPercentage: Number(discountValue) || 0,
        maxOff: Number(maximumOff) || 0,
        expirationTime: expirationValue,
        targetAmount: Number(minimumAmount) || 0
      }
    }

    editCartDiscount(discountData)
      .unwrap()
      .then(({ message }) => {
        if (message.toLowerCase() === 'success') {
          dispatch(
            addSystemNotification({
              message:
                mode === Mode.Create
                  ? t('offers.offers-creation.success')
                  : t('offers.offers-edition.success'),
              intent: 'success'
            })
          )
          setIsModalOpen(false)
          onSuccess()
          navigateOnSuccess(Mode.Edit)
        }
      })
      .catch(e => {
        dispatch(
          addSystemNotification({
            message:
              e?.message ||
              (mode === Mode.Create
                ? t('offers.offers-creation.error')
                : t('offers.offers-edition.error')),
            intent: 'error'
          })
        )
      })
  }

  const submitCreateDiscount = () => {
    const discountData = {
      discountType,
      amountPercentage: Number(discountValue) || 0,
      maxOff: Number(maximumOff) || 0,
      expirationTime: expirationValue,
      targetAmount: Number(minimumAmount) || 0
    }
    createCartDiscount(discountData)
      .unwrap()
      .then(({ message }) => {
        if (message.toLowerCase() === 'success') {
          dispatch(
            addSystemNotification({
              message:
                mode === Mode.Create
                  ? t('offers.offers-creation.success')
                  : t('offers.offers-edition.success'),
              intent: 'success'
            })
          )
          setIsModalOpen(false)
          onSuccess()
          navigateOnSuccess(Mode.Create)
        }
      })
      .catch(e => {
        dispatch(
          addSystemNotification({
            message:
              e?.message ||
              (mode === Mode.Create
                ? t('offers.offers-creation.error')
                : t('offers.offers-edition.error')),
            intent: 'error'
          })
        )
      })
  }

  return (
    <>
      <Header
        title={
          mode === Mode.Create
            ? t('offers.create-offer-title')
            : t('offers.edit-offer-title', { name })
        }
        onGoBack={handleGoBack}>
        <Button
          disabled={isSubmitButtonDisabled || isLoading}
          loading={isLoading}
          onClick={onOfferSubmit}
          shape="rounded"
          variant={isSubmitButtonDisabled ? 'secondary' : 'branded'}>
          {t('offers.offers-creation.save')}
        </Button>
      </Header>

      <div>
        <ChooseCartDiscountDetails
          discountType={discountType}
          onDiscountTypeChange={setDiscountType}
          discountValue={discountValue}
          onDiscountValueChange={setDiscountValue}
          minimumAmount={minimumAmount}
          onMinimumAmountChange={setMinimumAmount}
          maximumOff={maximumOff}
          onMaximumOffChange={setMaximumOff}
          expirationValue={expirationValue}
          onExpirationChange={setExpirationValue}
        />
      </div>

      <CommonModal
        open={isModalOpen}
        title="Confirm Discount Details"
        onOpenChange={() => {
          setIsModalOpen(!isModalOpen)
        }}
        body={
          <div className={Styles.paymentSummary}>
            <div className={Styles.summaryRow}>
              <Text variant="nextSubtitle">
                {t('offers.create-cart-discount.discount-type.title')}
              </Text>
              <div className={Styles.dots} />
              <Text variant="nextSubtitle">{discountType}</Text>
            </div>

            <div className={Styles.summaryRow}>
              <Text variant="nextSubtitle">
                {t('offers.create-cart-discount.discount-type.amount.title')}
              </Text>
              <div className={Styles.dots} />
              <Text variant="nextSubtitle">
                {discountType === DiscountType.Percentage
                  ? discountValue + ' %'
                  : discountValue + ' ' + currency}
              </Text>
            </div>

            <div className={Styles.summaryRow}>
              <Text variant="nextSubtitle">
                {t('offers.cart-discount.target-amount.title')}
              </Text>
              <div className={Styles.dots} />
              <Text variant="nextSubtitle">
                {minimumAmount + ' ' + currency}
              </Text>
            </div>

            {discountType === DiscountType.Percentage && (
              <div className={Styles.summaryRow}>
                <Text variant="nextSubtitle">
                  {t('offers.create-cart-discount.max-off.title')}
                </Text>
                <div className={Styles.dots} />
                <Text variant="nextSubtitle">
                  {maximumOff + ' ' + currency}
                </Text>
              </div>
            )}

            <div className={Styles.summaryRow}>
              <Text variant="nextSubtitle">
                {t('offers.create-cart-discount.expiration-date.title')}
              </Text>
              <div className={Styles.dots} />
              <Text variant="nextSubtitle">{expirationValue}</Text>
            </div>

            <div className={Styles.summaryRow}>
              <Text variant="nextCaption">
                *
                {t(
                  'offers.select-offer-type.discount-costs-billed-to-restaurant'
                )}
              </Text>
            </div>
          </div>
        }
        footer={
          <div className={Styles.footer}>
            <Button
              size="medium"
              variant="tertiary"
              type="button"
              onClick={() => setIsModalOpen(false)}>
              {t('common.modal.cancel')}
            </Button>
            <Button
              size="medium"
              variant="primary"
              type="button"
              disabled={isEditCartLoading}
              onClick={
                mode === Mode.Create ? submitCreateDiscount : submitEditDiscount
              }>
              {t('common.modal.confirm')}
            </Button>
          </div>
        }
      />
    </>
  )
}

export default CreateCartDiscountForm
